// ─────────────────────────────────────────────────────────────────────────────
// LocalStorageFtueStateStore
// ─────────────────────────────────────────────────────────────────────────────
// The v1 adapter. Requirement is user-scoped, localStorage is device-scoped, so
// one key holds a { [userId]: state } map rather than a key per user.
//
// Two reasons for the map over `…ftue.v1.<userId>`:
//   • `clearStorageExceptPreserved()` preserves an exact-key allowlist, and a
//     per-user suffix cannot be listed — every logout would wipe "seen".
//   • It mirrors DESKTOP_NOTIFICATION_PREFERENCE_KEY, which solved the same
//     problem the same way.
//
// The device caveat (a new browser may re-show a tip) is stated in the Settings
// copy rather than hidden.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';

import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { FTUE_STORAGE_KEY } from 'src/app/shared/preserved-storage-keys';
import type { FtueStateStore } from './ftue-state.store';
import { FTUE_DEFAULT_STATE, IFtueUserState } from './ftue.models';

/** Persisted shape: one entry per user id seen on this device. */
type FtueStateByUser = Record<string, IFtueUserState>;

/**
 * Bucket used when no user id is available (the sign-in screens, or a token
 * without a user). Keeps reads/writes from crashing; the real user's history is
 * never merged into it because the id is present everywhere tips can fire.
 */
const ANONYMOUS_BUCKET = 'anonymous';

@Injectable({ providedIn: 'root' })
export class LocalStorageFtueStateStore implements FtueStateStore {

  read(): IFtueUserState {
    const stored = this.readAll()[this.userKey()];
    if (!stored) {
      return { ...FTUE_DEFAULT_STATE, seen: {} };
    }
    // Defensive: a hand-edited or half-migrated entry must not break presenting.
    return {
      tipsEnabled: typeof stored.tipsEnabled === 'boolean' ? stored.tipsEnabled : true,
      seen: stored.seen && typeof stored.seen === 'object' ? { ...stored.seen } : {},
    };
  }

  write(state: IFtueUserState): void {
    try {
      LocalStorageService.put(FTUE_STORAGE_KEY, {
        ...this.readAll(),
        [this.userKey()]: state,
      });
    } catch {
      // Quota or disabled storage: tips are best-effort, never a blocker.
    }
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private readAll(): FtueStateByUser {
    try {
      const parsed = LocalStorageService.get(FTUE_STORAGE_KEY);
      return parsed && typeof parsed === 'object' ? (parsed as FtueStateByUser) : {};
    } catch {
      return {};
    }
  }

  /**
   * The signed-in user's id. Read from `currentUser` rather than from
   * `AuthService`, which lives under `features/` — core must not depend on a
   * feature to know who is looking at the screen.
   */
  private userKey(): string {
    try {
      const id = LocalStorageService.get('currentUser')?.user?.id;
      return id ? String(id) : ANONYMOUS_BUCKET;
    } catch {
      return ANONYMOUS_BUCKET;
    }
  }
}
